import { Component } from '@theme/component';

/**
 * A custom element that provides in-container zoom functionality for product images.
 * Click to zoom in, drag to pan, click again to zoom out.
 *
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom.
 * @property {HTMLElement} container - The container element.
 *
 * @extends Component<Refs>
 */
export class InlineZoom extends Component {
  requiredRefs = ['container'];

  #isZoomed = false;
  #scale = 2.5; // Zoom scale factor
  #isDragging = false;
  #hasDragged = false; // Track if actual dragging occurred
  #dragThreshold = 5; // Minimum pixels to consider as drag
  #startX = 0;
  #startY = 0;
  #initialX = 0; // Store initial position for drag detection
  #initialY = 0;
  #translateX = 0;
  #translateY = 0;
  #currentTranslateX = 0;
  #currentTranslateY = 0;
  #image = null;

  connectedCallback() {
    super.connectedCallback();
    this.#findImage();
    if (this.#image) {
      this.#setupEventListeners();
      this.#setupStyles();
    }
  }

  #findImage() {
    // Look for image with ref="image" first, then any img element
    this.#image = this.refs.container.querySelector('[ref="image"] img') || 
                  this.refs.container.querySelector('img');
  }

  #setupStyles() {
    // Add necessary styles to container and image
    this.refs.container.style.position = 'relative';
    this.refs.container.style.overflow = 'hidden';
    this.refs.container.style.cursor = 'zoom-in';
    
    if (this.#image) {
      this.#image.style.transition = 'transform 0.3s ease';
      this.#image.style.transformOrigin = 'center center';
    }
  }

  #setupEventListeners() {
    if (!this.#image) return;
    
    // Click to zoom/unzoom
    this.#image.addEventListener('click', this.#handleClick);
    
    // Mouse events for dragging when zoomed
    this.#image.addEventListener('mousedown', this.#handleMouseDown);
    document.addEventListener('mousemove', this.#handleMouseMove);
    document.addEventListener('mouseup', this.#handleMouseUp);
    
    // Touch events for mobile
    this.#image.addEventListener('touchstart', this.#handleTouchStart, { passive: false });
    this.#image.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    this.#image.addEventListener('touchend', this.#handleTouchEnd);
  }

  #handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Don't zoom if we just finished dragging
    if (this.#hasDragged) {
      this.#hasDragged = false;
      return;
    }
    
    if (!this.#isZoomed) {
      this.#zoomIn(e);
    } else {
      this.#zoomOut();
    }
  };

  #zoomIn(e) {
    if (!this.#image) return;
    
    const rect = this.refs.container.getBoundingClientRect();
    const imageRect = this.#image.getBoundingClientRect();
    
    // Calculate click position relative to image
    const clickX = e.clientX - imageRect.left;
    const clickY = e.clientY - imageRect.top;
    
    // Calculate zoom origin as percentage
    const originX = (clickX / imageRect.width) * 100;
    const originY = (clickY / imageRect.height) * 100;
    
    this.#image.style.transformOrigin = `${originX}% ${originY}%`;
    this.#image.style.transform = `scale(${this.#scale})`;
    this.refs.container.style.cursor = 'grab';
    
    this.#isZoomed = true;
    this.#translateX = 0;
    this.#translateY = 0;
    this.#currentTranslateX = 0;
    this.#currentTranslateY = 0;
  }

  #zoomOut() {
    if (!this.#image) return;
    
    this.#image.style.transform = 'scale(1) translate(0, 0)';
    this.#image.style.transformOrigin = 'center center';
    this.refs.container.style.cursor = 'zoom-in';
    
    this.#isZoomed = false;
    this.#translateX = 0;
    this.#translateY = 0;
    this.#currentTranslateX = 0;
    this.#currentTranslateY = 0;
    this.#hasDragged = false;
  }

  #handleMouseDown = (e) => {
    if (!this.#isZoomed || !this.#image) return;
    
    e.preventDefault();
    this.#isDragging = true;
    this.#hasDragged = false; // Reset drag state
    this.#startX = e.clientX - this.#translateX;
    this.#startY = e.clientY - this.#translateY;
    this.#initialX = e.clientX; // Store initial position
    this.#initialY = e.clientY;
    this.refs.container.style.cursor = 'grabbing';
    this.#image.style.transition = 'none';
  };

  #handleMouseMove = (e) => {
    if (!this.#isDragging || !this.#isZoomed || !this.#image) return;
    
    e.preventDefault();
    
    // Calculate distance moved from initial position
    const deltaX = Math.abs(e.clientX - this.#initialX);
    const deltaY = Math.abs(e.clientY - this.#initialY);
    
    // If we've moved more than the threshold, it's a drag
    if (deltaX > this.#dragThreshold || deltaY > this.#dragThreshold) {
      this.#hasDragged = true;
    }
    
    this.#translateX = e.clientX - this.#startX;
    this.#translateY = e.clientY - this.#startY;
    
    // Constrain movement to prevent image from moving too far
    this.#constrainMovement();
    
    this.#image.style.transform = `scale(${this.#scale}) translate(${this.#translateX}px, ${this.#translateY}px)`;
  };

  #handleMouseUp = () => {
    if (!this.#isZoomed || !this.#image) return;
    
    this.#isDragging = false;
    this.refs.container.style.cursor = 'grab';
    this.#image.style.transition = 'transform 0.3s ease';
    this.#currentTranslateX = this.#translateX;
    this.#currentTranslateY = this.#translateY;
  };

  #handleTouchStart = (e) => {
    if (!this.#isZoomed || !this.#image) return;
    
    e.preventDefault();
    const touch = e.touches[0];
    this.#isDragging = true;
    this.#hasDragged = false; // Reset drag state
    this.#startX = touch.clientX - this.#translateX;
    this.#startY = touch.clientY - this.#translateY;
    this.#initialX = touch.clientX; // Store initial position
    this.#initialY = touch.clientY;
    this.#image.style.transition = 'none';
  };

  #handleTouchMove = (e) => {
    if (!this.#isDragging || !this.#isZoomed || !this.#image) return;
    
    e.preventDefault();
    const touch = e.touches[0];
    
    // Calculate distance moved from initial position
    const deltaX = Math.abs(touch.clientX - this.#initialX);
    const deltaY = Math.abs(touch.clientY - this.#initialY);
    
    // If we've moved more than the threshold, it's a drag
    if (deltaX > this.#dragThreshold || deltaY > this.#dragThreshold) {
      this.#hasDragged = true;
    }
    
    this.#translateX = touch.clientX - this.#startX;
    this.#translateY = touch.clientY - this.#startY;
    
    this.#constrainMovement();
    
    this.#image.style.transform = `scale(${this.#scale}) translate(${this.#translateX}px, ${this.#translateY}px)`;
  };

  #handleTouchEnd = () => {
    if (!this.#isZoomed || !this.#image) return;
    
    this.#isDragging = false;
    this.#image.style.transition = 'transform 0.3s ease';
    this.#currentTranslateX = this.#translateX;
    this.#currentTranslateY = this.#translateY;
  };

  #constrainMovement() {
    if (!this.#image) return;
    
    // Since all images are portrait and same size, we can use optimized constraints
    // Portrait images scaled 2.5x will have more vertical excess than horizontal
    const maxTranslateX = 50;  // Limited horizontal movement for portrait images
    const maxTranslateY = 50; // More vertical movement since images are tall
    
    // Constrain translation
    this.#translateX = Math.max(-maxTranslateX, Math.min(maxTranslateX, this.#translateX));
    this.#translateY = Math.max(-maxTranslateY, Math.min(maxTranslateY, this.#translateY));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up event listeners
    document.removeEventListener('mousemove', this.#handleMouseMove);
    document.removeEventListener('mouseup', this.#handleMouseUp);
  }
}

if (!customElements.get('inline-zoom')) {
  customElements.define('inline-zoom', InlineZoom);
}